import React, { useState, useEffect } from 'react';
import './App.css';
import LandingHeader from './components/LandingHeader';
import LandingHero from './components/LandingHero';
import LandingFooter from './components/LandingFooter';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const FAVICON_URL = `${process.env.PUBLIC_URL || ''}/drive-logo-favicon-v1.png`;
const LOGO_WIDE_URL = `${process.env.PUBLIC_URL || ''}/drive-logo-wide-v1.png`;
const HOME_HREF = `${process.env.PUBLIC_URL || ''}/`;

const TAB_COPY = {
  dashboard: {
    kicker: 'Status',
    title: 'Systemübersicht',
    description: 'Überprüfe den Systemzustand und starte direkt mit deinen Aufgaben.'
  },
  form: {
    kicker: 'Datenerfassung',
    title: 'Leuchtturm Metrik erfassen',
    description: 'Pflege Monatszahlen mit bestmöglicher Transparenz und erhalte sofortiges Feedback.'
  },
  mapping: {
    kicker: 'Produktpflege',
    title: 'Produkt-Mappings',
    description: 'Verwalte Produkt-Mappings konsistent über alle Apps hinweg.'
  },
  admin: {
    kicker: 'Teamverwaltung',
    title: 'Adminbereich',
    description: 'Verwalte Konten und Zugriffe, ohne den Datenfluss zu unterbrechen.'
  }
};

const createEmptyFormData = () => ({
  web_abos_data: [
    { abo_type: 'PK1', aktuell: 0, zugaenge: 0, abgaenge: 0 },
    { abo_type: 'PK2', aktuell: 0, zugaenge: 0, abgaenge: 0 },
    { abo_type: 'PK3', aktuell: 0, zugaenge: 0, abgaenge: 0 }
  ],
  epaper_abos_data: [
    { abo_type: 'ePaper', aktuell: 0, zugaenge: 0, abgaenge: 0 }
  ]
});

function MetricNumberInput({ value, onChange, className }) {
  const [isFocused, setIsFocused] = React.useState(false);
  const displayValue = isFocused && value === 0 ? '' : String(value);

  const handleChange = (e) => {
    const raw = e.target.value;
    if (raw === '') {
      onChange(0);
      return;
    }
    if (!/^\d+$/.test(raw)) {
      return;
    }
    onChange(parseInt(raw, 10));
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      value={displayValue}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onChange={handleChange}
      className={className}
    />
  );
}

const getPKTitle = (pkType) => {
  const titles = {
    'PK1': 'Preiskorridor 7,00 € und mehr pro Monat',
    'PK2': 'Preiskorridor 2,00 € bis 6,99 € pro Monat',
    'PK3': 'Preiskorridor bis 1,99 € pro Monat',
    'ePaper': 'Preiskorridor 2,00 € und mehr pro Monat'
  };
  return titles[pkType] || '';
};

const getPKTooltip = (pkType) => {
  const tooltips = {
    'PK1': 'In diesem Feld werden alle Web-Abos in dieser Preisklasse erfasst, unabhängig ob Standalone oder als Add-on zu einem Print-/ePaper-Abo. Bei den Abgängen der Web-Abos PK1 sollten auch in niedrigpreisigere Web-Abos der PK2/PK3 übergegangene Abos mit aufgenommen werden. Diese tauchen dann als Zugang in PK2/PK3 und als Abgang in PK1 auf.',
    'PK2': 'In diesem Feld werden alle Web-Abos in dieser Preisklasse erfasst, unabhängig ob Standalone oder als Add-on zu einem Print-/ePaper-Abo. Bei den Abgängen der rabattierten Web-Abos in PK2 sollten auch in höher- oder niedrigpreisigere Web-Abos der PK1/PK3 übergegangene Abos mit aufgenommen werden. Diese tauchen dann als Zugang in PK1/PK3 und als Abgang in PK2 auf.',
    'PK3': 'In diesem Feld werden alle Web-Abos in dieser Preisklasse erfasst, unabhängig ob Standalone oder als Add-on zu einem Print-/ePaper-Abo. Bei den Abgängen der rabattierten Web-Abos PK3 sollten auch in hochpreisigere Web-Abos der PK1/PK2 übergegangene Abos mit aufgenommen werden. Diese tauchen dann als Zugang in PK1/PK2 und als Abgang in PK3 auf.',
    'ePaper': 'In diesem Feld werden ePaper als Hauptbestandteil des Abos im Preiskorridor 2,00 € pro Monat und mehr erfasst (z. B. reine ePaper-Abos, ePaper-Web-Bundle, ePaper mit Print-Wochenendausgabe).'
  };
  return tooltips[pkType] || '';
};

// Component for editing missing product rows
function MissingProductRow({ product, onUpdate, forceEditing = false, onExitEdit, saveAllTrigger = 0, onSaveComplete }) {
  const getInitialDrive = () => {
    const drive = product.subscription_conversion_product_drive || '';
    return drive === '(unbekannt)' ? '' : drive;
  };
  
  const getInitialCategory = () => {
    const category = product.subscription_conversion_product_category || '';
    return category === '(unbekannt)' ? '' : category;
  };

  const [driveValue, setDriveValue] = useState(getInitialDrive());
  const [categoryValue, setCategoryValue] = useState(getInitialCategory());
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const effectiveEditing = forceEditing || isEditing;
  const saveAllTriggerRef = React.useRef(0);

  const handleSave = React.useCallback(async () => {
    if (!driveValue.trim() || !categoryValue.trim()) {
      if (!forceEditing) alert('Bitte Drive und Kategorie ausfüllen');
      onSaveComplete?.();
      return;
    }
    setSaving(true);
    const success = await onUpdate(product, driveValue.trim(), categoryValue.trim());
    if (success) {
      setIsEditing(false);
      onExitEdit?.();
    }
    setSaving(false);
    onSaveComplete?.();
  }, [driveValue, categoryValue, product, onUpdate, onExitEdit, onSaveComplete, forceEditing]);

  React.useEffect(() => {
    if (saveAllTrigger > 0 && saveAllTrigger !== saveAllTriggerRef.current && forceEditing) {
      saveAllTriggerRef.current = saveAllTrigger;
      handleSave();
    }
  }, [saveAllTrigger, forceEditing, handleSave]);

  const handleCancel = () => {
    setDriveValue(getInitialDrive());
    setCategoryValue(getInitialCategory());
    setIsEditing(false);
    onExitEdit?.();
  };

  const displayDrive = driveValue || product.subscription_conversion_product_drive || '(unbekannt)';
  const displayCategory = categoryValue || product.subscription_conversion_product_category || '(unbekannt)';
  const isMissing = displayDrive === '(unbekannt)' || displayCategory === '(unbekannt)';

  const getConversionCellClass = () => {
    const count = product.anzahl_conversions_dcc30;
    if (count == null) return '';
    if (count > 50) return 'conversion-high-red';
    if (count >= 10) return 'conversion-high-yellow';
    return '';
  };

  return (
    <tr>
      <td>{product.subscription_conversion_product_publisher}</td>
      <td className={getConversionCellClass()}>
        {product.anzahl_conversions_dcc30 != null ? product.anzahl_conversions_dcc30.toLocaleString('de-DE') : '-'}
      </td>
      <td>
        {effectiveEditing ? (
          <select
            value={driveValue}
            onChange={(e) => setDriveValue(e.target.value)}
            className="form-control"
          >
            <option value="">Bitte wählen...</option>
            <option value="Probeabo">Probeabo</option>
            <option value="Vollabo">Vollabo</option>
            <option value="Sonstiges">Sonstiges</option>
          </select>
        ) : (
          <span className={isMissing && displayDrive === '(unbekannt)' ? 'missing-field' : ''}>
            {displayDrive}
          </span>
        )}
      </td>
      <td>
        {effectiveEditing ? (
          <select
            value={categoryValue}
            onChange={(e) => setCategoryValue(e.target.value)}
            className="form-control"
          >
            <option value="">Bitte wählen...</option>
            <option value="Online">Online</option>
            <option value="ePaper">ePaper</option>
            <option value="Print">Print</option>
            <option value="Sonstiges">Sonstiges</option>
          </select>
        ) : (
          <span className={isMissing && displayCategory === '(unbekannt)' ? 'missing-field' : ''}>
            {displayCategory}
          </span>
        )}
      </td>
      <td>
        {effectiveEditing ? (
          forceEditing ? (
            <span className="table-action-placeholder">—</span>
          ) : (
            <div className="table-actions">
              <button
                type="button"
                onClick={handleSave}
                className="btn btn-sm btn-primary"
                disabled={saving}
              >
                {saving ? 'Speichern...' : 'Speichern'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="btn btn-sm btn-secondary"
                disabled={saving}
              >
                Abbrechen
              </button>
            </div>
          )
        ) : (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="btn btn-sm btn-outline"
          >
            Bearbeiten
          </button>
        )}
      </td>
    </tr>
  );
}

function ExistingProductRow({ product, appId, onUpdate, forceEditing = false, onExitEdit, saveAllTrigger = 0, onSaveComplete }) {
  const [driveValue, setDriveValue] = useState(product.subscription_conversion_product_drive || '');
  const [categoryValue, setCategoryValue] = useState(product.subscription_conversion_product_category || '');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const effectiveEditing = forceEditing || isEditing;
  const saveAllTriggerRef = React.useRef(0);

  const handleSave = React.useCallback(async () => {
    if (!driveValue.trim() || !categoryValue.trim()) {
      if (!forceEditing) alert('Bitte Aboart und Kategorie ausfüllen');
      onSaveComplete?.();
      return;
    }
    setSaving(true);
    const success = await onUpdate(appId, product.subscription_conversion_product_publisher, driveValue.trim(), categoryValue.trim());
    if (success) {
      setIsEditing(false);
      onExitEdit?.();
    }
    setSaving(false);
    onSaveComplete?.();
  }, [driveValue, categoryValue, appId, product.subscription_conversion_product_publisher, onUpdate, onExitEdit, onSaveComplete, forceEditing]);

  React.useEffect(() => {
    if (saveAllTrigger > 0 && saveAllTrigger !== saveAllTriggerRef.current && forceEditing) {
      saveAllTriggerRef.current = saveAllTrigger;
      handleSave();
    }
  }, [saveAllTrigger, forceEditing, handleSave]);

  const handleCancel = () => {
    setDriveValue(product.subscription_conversion_product_drive || '');
    setCategoryValue(product.subscription_conversion_product_category || '');
    setIsEditing(false);
    onExitEdit?.();
  };

  return (
    <tr>
      <td>{product.subscription_conversion_product_publisher}</td>
      <td>
        {effectiveEditing ? (
          <select value={driveValue} onChange={(e) => setDriveValue(e.target.value)} className="form-control">
            <option value="">Bitte wählen...</option>
            <option value="Vollabo">Vollabo</option>
            <option value="Probeabo">Probeabo</option>
            <option value="Sonstiges">Sonstiges</option>
          </select>
        ) : (
          driveValue
        )}
      </td>
      <td>
        {effectiveEditing ? (
          <select value={categoryValue} onChange={(e) => setCategoryValue(e.target.value)} className="form-control">
            <option value="">Bitte wählen...</option>
            <option value="Online">Online</option>
            <option value="ePaper">ePaper</option>
            <option value="Print">Print</option>
            <option value="Sonstiges">Sonstiges</option>
          </select>
        ) : (
          categoryValue
        )}
      </td>
      <td>
        {effectiveEditing ? (
          forceEditing ? (
            <span className="table-action-placeholder">—</span>
          ) : (
            <div className="table-actions">
              <button type="button" onClick={handleSave} className="btn btn-sm btn-primary" disabled={saving}>
                {saving ? 'Speichern...' : 'Speichern'}
              </button>
              <button type="button" onClick={handleCancel} className="btn btn-sm btn-secondary" disabled={saving}>
                Abbrechen
              </button>
            </div>
          )
        ) : (
          <button type="button" onClick={() => setIsEditing(true)} className="btn btn-sm btn-outline">
            Bearbeiten
          </button>
        )}
      </td>
    </tr>
  );
}

function App() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [users, setUsers] = useState([]);
  const [newUserForm, setNewUserForm] = useState({ username: '', name: '', group: '' });
  const [health, setHealth] = useState(null);
  const [selectedPublisher, setSelectedPublisher] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState('');
  const [missingMonths, setMissingMonths] = useState([]);
  const [hasExistingData, setHasExistingData] = useState(false);
  const [existingDataWarning, setExistingDataWarning] = useState('');
  const [formData, setFormData] = useState(createEmptyFormData);
  const [selectedAppId, setSelectedAppId] = useState('');
  const [mappingForm, setMappingForm] = useState({ product_name: '', abo_type: '', product_category: '' });
  const oauthCallbackProcessed = React.useRef(false);
  const keycloakRedirectAttempted = React.useRef(false);
  const [appIdSearch, setAppIdSearch] = useState('');
  const [mappingMode, setMappingMode] = useState('fill-missing'); // 'fill-missing' or 'create-new'
  const [missingProducts, setMissingProducts] = useState([]);
  const [loadingMissingProducts, setLoadingMissingProducts] = useState(false);
  const [keycloakEnabled, setKeycloakEnabled] = useState(false);
  const [allAppIds, setAllAppIds] = useState([]);
  const [loadingAppIds, setLoadingAppIds] = useState(false);
  const [existingProducts, setExistingProducts] = useState([]);
  const [loadingExistingProducts, setLoadingExistingProducts] = useState(false);
  const [productsView, setProductsView] = useState('missing');
  const [editingMissingKeys, setEditingMissingKeys] = useState(new Set());
  const [editingExistingKeys, setEditingExistingKeys] = useState(new Set());
  const [saveAllMissingTrigger, setSaveAllMissingTrigger] = useState(0);
  const [saveAllExistingTrigger, setSaveAllExistingTrigger] = useState(0);
  const [savingAll, setSavingAll] = useState(false);
  const saveAllRemainingRef = React.useRef(0);
  const [processingOAuthCallback, setProcessingOAuthCallback] = useState(false); // 'existing' or 'missing'
  const [isScrolled, setIsScrolled] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const currentTabCopy = TAB_COPY[activeTab] || TAB_COPY.dashboard;

  useEffect(() => {
    const existingLink = document.querySelector("link[rel='icon']");
    const favicon = existingLink || document.createElement('link');
    favicon.rel = 'icon';
    favicon.type = 'image/png';
    favicon.href = FAVICON_URL;
    if (!existingLink) {
      document.head.appendChild(favicon);
    }
  }, []);

  const pageDescription = React.useMemo(() => {
    if (activeTab === 'form') {
      if (selectedPublisher) {
        const monthLabel = selectedMonth || 'Monat offen';
        return `Bearbeite ${selectedPublisher} · ${monthLabel} ${selectedYear}.`;
      }
      return 'Wähle einen Publisher und Monat, um neue Daten zu erfassen.';
    }

    if (activeTab === 'mapping') {
      if (mappingMode === 'fill-missing') {
        if (selectedAppId) {
          const missingCount = missingProducts.length;
          if (missingCount > 0) {
            return `Fülle ${missingCount} fehlende Zuordnung${missingCount === 1 ? '' : 'en'} für ${selectedAppId}.`;
          }
          return `Alle Produkte für ${selectedAppId} sind gemappt – prüfe Bestandsdaten.`;
        }
        return 'Wähle eine App ID aus, um fehlende Produkt-Mappings zu prüfen.';
      }

      if (selectedAppId) {
        return `Lege ein neues Produkt für ${selectedAppId} an und weise den passenden Typ zu.`;
      }

      return 'Starte mit der Auswahl einer App ID, um neue Produkte anzulegen.';
    }

    return currentTabCopy.description;
  }, [
    activeTab,
    currentTabCopy,
    mappingMode,
    missingProducts,
    selectedAppId,
    selectedMonth,
    selectedPublisher,
    selectedYear
  ]);

  const formSummary = React.useMemo(() => {
    if (activeTab !== 'form') return null;
    if (!selectedPublisher) return 'Kein Publisher ausgewählt';
    const monthLabel = selectedMonth || 'Monat offen';
    return `${selectedPublisher} · ${monthLabel} ${selectedYear}`;
  }, [activeTab, selectedMonth, selectedPublisher, selectedYear]);

  const mappingSummary = React.useMemo(() => {
    if (activeTab !== 'mapping') return null;
    return selectedAppId ? `App ${selectedAppId}` : 'Keine App ID gewählt';
  }, [activeTab, selectedAppId]);

  const isFormReady = Boolean(selectedPublisher && selectedMonth);
  const primaryActionLabel = hasExistingData ? 'Daten aktualisieren' : 'Daten absenden';

  const handleUnauthorized = () => {
    setIsAuthenticated(false);
    setUser(null);
    setShowAdminPanel(false);
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setLoginError('Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.');
    setActiveTab('dashboard');
    setError(null);
  };

  const fetchData = async () => {
    const shouldSurfaceHealthError = () => Boolean(localStorage.getItem('authToken'));
    try {
      const response = await fetch(`${API_BASE}/health`);
      if (response.ok) {
        const data = await response.json();
        setHealth(data);
        setError(null);
      } else if (shouldSurfaceHealthError()) {
        setError('Fehler beim Laden der Daten');
      }
    } catch (err) {
      if (shouldSurfaceHealthError()) {
        setError('Fehler beim Laden der Daten');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAllAppIds = async () => {
    try {
      setLoadingAppIds(true);
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('No auth token available');
        setAllAppIds([]);
        return;
      }
      
      const response = await fetch(`${API_BASE}/app-mappings/all-app-ids?token=${token}`);
      
      if (response.status === 401) { handleUnauthorized(); return; }

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Failed to fetch app IDs: ${response.status} ${response.statusText}`, errorText);
        setAllAppIds([]);
        return;
      }
      
      const data = await response.json();
      const appIds = data.app_ids || [];
      console.log(`Loaded ${appIds.length} app IDs from BigQuery`);
      setAllAppIds(appIds);
    } catch (err) {
      console.error('Failed to fetch app IDs:', err);
      setAllAppIds([]);
    } finally {
      setLoadingAppIds(false);
    }
  };

  const fetchUsers = async () => {
    try {
      // Only fetch users if the current user is an admin
      if (!user?.username || user.client_role !== 'admin') {
        return;
      }
      
      const response = await fetch(`${API_BASE}/admin/users?admin_username=${user.username}`);
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setIsAuthenticated(true);
        setUser(data.user_info);
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('user', JSON.stringify(data.user_info));
        
        // Show admin panel if user has admin role
        if (data.user_info?.client_role === 'admin') {
          setShowAdminPanel(true);
          fetchUsers();
        }
      } else {
      setLoginError(data.message || 'Anmeldung fehlgeschlagen');
      }
    } catch (err) {
      setLoginError('Anmeldung fehlgeschlagen: ' + err.message);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUser(null);
    setShowAdminPanel(false);
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setLoginForm({ username: '', password: '' });
    setActiveTab('dashboard');
    setError(null);
    setLoginError('');
  };

  const fetchMissingMonths = async (publisher, group, year) => {
    try {
      if (!user?.allowed_publisher_ids?.[publisher]) {
        console.error('Publisher not found in user permissions');
        return;
      }
      
      const publisherData = user.allowed_publisher_ids[publisher];
      const actualPublisher = Object.keys(user.allowed_publisher_ids).find(key => 
        user.allowed_publisher_ids[key]?.publisher_id === publisherData?.publisher_id && 
        key !== user.username
      ) || publisher;
      
      const response = await fetch(`${API_BASE}/subscription/missing-months?publisher=${actualPublisher}&group=${group}&year=${year}&username=${user.username}`);
      if (response.ok) {
        const data = await response.json();
        setMissingMonths(data.missing_months);
      }
    } catch (error) {
      console.error('Error fetching missing months:', error);
    }
  };

  const fetchExistingData = async (publisher, publisherId, group, year, month) => {
    try {
      const actualPublisher = publisher;
      
      const requestBody = {
        publisher: actualPublisher,
        publisher_id: publisherId,
        group,
        year,
        month
      };
      
      const response = await fetch(`${API_BASE}/form/existing-data?username=${user.username}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.has_existing_data) {
          setHasExistingData(true);
          setExistingDataWarning(`Bestehende Daten für ${publisher} – ${month} ${year} werden bearbeitet. Du kannst die Werte unten anpassen.`);
        if (data.form_data) {
          setFormData(data.form_data);
        }
      } else {
        setHasExistingData(false);
        setExistingDataWarning('');
        setFormData(createEmptyFormData());
      }
      } else {
        const errorText = await response.text();
        console.error('API error:', response.status, errorText);
      }
    } catch (error) {
      console.error('Error fetching existing data:', error);
      setHasExistingData(false);
      setExistingDataWarning('');
    }
  };

  const submitForm = async () => {
    try {
      if (!selectedPublisher || !user?.allowed_publisher_ids?.[selectedPublisher]) {
        alert('Bitte wähle einen gültigen Publisher');
        return;
      }
      
      const publisherData = user.allowed_publisher_ids[selectedPublisher];
      
      if (!publisherData?.publisher_id) {
        alert('Ungültige Publisher-Daten');
        return;
      }
      
      const actualPublisher = Object.keys(user.allowed_publisher_ids).find(key => 
        user.allowed_publisher_ids[key]?.publisher_id === publisherData.publisher_id && 
        key !== user.username
      ) || selectedPublisher;
      
      const response = await fetch(`${API_BASE}/form/submit?username=${user.username}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          publisher: actualPublisher,
          publisher_id: publisherData.publisher_id,
          group: publisherData.group,
          year: selectedYear,
          month: selectedMonth,
          form_data: formData
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          alert('Daten erfolgreich übermittelt!');
          fetchMissingMonths(selectedPublisher, publisherData.group, selectedYear);
          setFormData(createEmptyFormData());
          setHasExistingData(false);
          setExistingDataWarning('');
        } else {
          alert(`Übermittlung fehlgeschlagen: ${data.message}`);
        }
      } else {
        const errorData = await response.json();
        alert(`Übermittlung fehlgeschlagen: ${errorData.detail || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert(`Übermittlung fehlgeschlagen: ${error.message}`);
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE}/admin/users/create?admin_username=${user.username}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newUserForm),
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert(`Nutzer erfolgreich erstellt! Benutzername: ${data.username}, Passwort: ${data.password}`);
        setNewUserForm({ username: '', name: '', group: '' });
        fetchUsers();
      } else {
        alert(`Nutzer konnte nicht erstellt werden: ${data.message}`);
      }
    } catch (err) {
      alert(`Nutzer konnte nicht erstellt werden: ${err.message}`);
    }
  };

  const deleteUser = async (username) => {
    if (window.confirm(`Soll der Nutzer ${username} wirklich gelöscht werden?`)) {
      try {
        const response = await fetch(`${API_BASE}/admin/users/${username}?admin_username=${user.username}`, {
          method: 'DELETE',
        });
        
        const data = await response.json();
        
        if (data.success) {
          alert('Nutzer erfolgreich gelöscht!');
          fetchUsers();
        } else {
          alert(`Nutzer konnte nicht gelöscht werden: ${data.message}`);
        }
      } catch (err) {
        alert(`Nutzer konnte nicht gelöscht werden: ${err.message}`);
      }
    }
  };

  const handleAppIdSelect = (appId) => {
    setSelectedAppId(appId);
    setMappingForm({ product_name: '', abo_type: '', product_category: '' });
    setProductsView('missing'); // Reset to missing products view (first in row)
    // Always fetch existing products when app is selected
    fetchExistingProducts(appId);
    if (mappingMode === 'fill-missing') {
      fetchMissingProducts(appId);
    }
  };

  const openAllMissingForEdit = () => {
    setEditingMissingKeys(new Set(missingProducts.map((p) => `${p.app_id}-${p.subscription_conversion_product_publisher}`)));
  };
  const closeAllMissingEdit = () => setEditingMissingKeys(new Set());
  const openAllExistingForEdit = () => {
    setEditingExistingKeys(new Set(existingProducts.map((p) => p.subscription_conversion_product_publisher)));
  };
  const closeAllExistingEdit = () => setEditingExistingKeys(new Set());

  const handleSaveAllMissing = () => {
    const count = editingMissingKeys.size;
    if (count === 0) return;
    setSavingAll(true);
    saveAllRemainingRef.current = count;
    setSaveAllMissingTrigger((t) => t + 1);
  };
  const handleSaveAllExisting = () => {
    const count = editingExistingKeys.size;
    if (count === 0) return;
    setSavingAll(true);
    saveAllRemainingRef.current = count;
    setSaveAllExistingTrigger((t) => t + 1);
  };
  const onSaveAllComplete = () => {
    saveAllRemainingRef.current -= 1;
    if (saveAllRemainingRef.current <= 0) setSavingAll(false);
  };

  const fetchExistingProducts = async (appId) => {
    if (!appId) return;
    setLoadingExistingProducts(true);
    try {
      const response = await fetch(`${API_BASE}/subscription/products/${appId}?token=${localStorage.getItem('authToken')}`);
      if (response.status === 401) { handleUnauthorized(); return; }
      if (response.ok) {
        const data = await response.json();
        setExistingProducts(data.existing_products || []);
      } else {
        console.error('Failed to fetch existing products');
        setExistingProducts([]);
      }
    } catch (err) {
      console.error('Error fetching existing products:', err);
      setExistingProducts([]);
    } finally {
      setLoadingExistingProducts(false);
    }
  };

  const fetchMissingProducts = async (appId) => {
    if (!appId) return;
    setLoadingMissingProducts(true);
    try {
      const response = await fetch(`${API_BASE}/subscription/missing-products/${appId}?token=${localStorage.getItem('authToken')}`);
      if (response.status === 401) { handleUnauthorized(); return; }
      if (response.ok) {
        const data = await response.json();
        setMissingProducts(data.missing_products || []);
      } else {
        console.error('Failed to fetch missing products');
        setMissingProducts([]);
      }
    } catch (err) {
      console.error('Error fetching missing products:', err);
      setMissingProducts([]);
    } finally {
      setLoadingMissingProducts(false);
    }
  };

  const updateMissingProduct = async (product, driveValue, categoryValue) => {
    try {
      const response = await fetch(`${API_BASE}/subscription/mapping/add?token=${localStorage.getItem('authToken')}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          app_id: product.app_id,
          subscription_conversion_product_publisher: product.subscription_conversion_product_publisher,
          subscription_conversion_product_drive: driveValue,
          subscription_conversion_product_category: categoryValue
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Refresh missing products and existing products
        await fetchMissingProducts(product.app_id);
        await fetchExistingProducts(product.app_id);
        return true;
      } else {
        alert(`Produkt konnte nicht aktualisiert werden: ${data.message}`);
        return false;
      }
    } catch (err) {
      alert(`Produkt konnte nicht aktualisiert werden: ${err.message}`);
      return false;
    }
  };

  const updateExistingProduct = async (appId, publisherName, driveValue, categoryValue) => {
    try {
      const response = await fetch(`${API_BASE}/subscription/mapping/add?token=${localStorage.getItem('authToken')}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          app_id: appId,
          subscription_conversion_product_publisher: publisherName,
          subscription_conversion_product_drive: driveValue,
          subscription_conversion_product_category: categoryValue
        })
      });
      const data = await response.json();
      if (data.success) {
        await fetchExistingProducts(appId);
        return true;
      } else {
        alert(`Produkt konnte nicht aktualisiert werden: ${data.message}`);
        return false;
      }
    } catch (err) {
      alert(`Produkt konnte nicht aktualisiert werden: ${err.message}`);
      return false;
    }
  };

  const filteredAppIds = (allAppIds.length > 0 ? allAppIds : (user?.allowed_app_ids || [])).filter(appId => 
    appId.toLowerCase().includes(appIdSearch.toLowerCase())
  );

  const createMapping = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE}/subscription/mapping/add?token=${localStorage.getItem('authToken')}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          app_id: selectedAppId,
          subscription_conversion_product_publisher: mappingForm.product_name,
          subscription_conversion_product_drive: mappingForm.abo_type,
          subscription_conversion_product_category: mappingForm.product_category
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('Mapping erfolgreich erstellt!');
        setMappingForm({ product_name: '', abo_type: '', product_category: '' });
        // Refresh existing products after creating new mapping
        if (selectedAppId) {
          fetchExistingProducts(selectedAppId);
        }
        if (mappingMode === 'fill-missing' && selectedAppId) {
          fetchMissingProducts(selectedAppId);
        } else {
        setSelectedAppId('');
        }
      } else {
        alert(`Mapping konnte nicht erstellt werden: ${data.message}`);
      }
    } catch (err) {
      alert(`Mapping konnte nicht erstellt werden: ${err.message}`);
    }
  };

  const checkKeycloakConfig = async () => {
    try {
      const response = await fetch(`${API_BASE}/auth/config`);
      if (response.ok) {
        const data = await response.json();
        console.log('Keycloak config received:', data.keycloak);
        setKeycloakEnabled(data.keycloak?.enabled === true);
      } else {
        console.error('Failed to fetch Keycloak config:', response.status);
      }
    } catch (err) {
      console.error('Failed to check Keycloak config:', err);
    }
  };

  const handleKeycloakLogin = async () => {
    try {
      const response = await fetch(`${API_BASE}/auth/keycloak/login`);
      if (response.ok) {
        const data = await response.json();
        if (data.auth_url) {
          window.location.href = data.auth_url;
        }
      } else {
        const errorData = await response.json();
        setLoginError(errorData.detail || 'Keycloak-Anmeldung konnte nicht gestartet werden');
      }
    } catch (err) {
      setLoginError('Keycloak-Anmeldung konnte nicht gestartet werden: ' + err.message);
    }
  };

  const handleOAuthCallback = async () => {
    // Prevent duplicate processing (React StrictMode in dev causes double render)
    if (oauthCallbackProcessed.current) {
      console.log('[DEBUG] OAuth callback already processed, skipping duplicate call');
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    if (error) {
      setLoginError(`OAuth-Fehler: ${error}${errorDescription ? ' - ' + errorDescription : ''}`);
      oauthCallbackProcessed.current = false;
      setProcessingOAuthCallback(false);
      return;
    }

    if (!code) {
      oauthCallbackProcessed.current = false;
      setProcessingOAuthCallback(false);
      return;
    }

    // Mark as processed immediately to prevent duplicate calls and auto-redirects
    oauthCallbackProcessed.current = true;
    // Also set redirect flag to prevent auto-redirect during callback processing
    keycloakRedirectAttempted.current = true;
    // Set processing state to show loading instead of login screen
    setProcessingOAuthCallback(true);

    try {
      setLoginError('');
      const response = await fetch(`${API_BASE}/auth/keycloak/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.token) {
          const clientRole = data.user_info?.client_role;
          
          // Check if user has a valid role
          if (!clientRole) {
            setLoginError('Zugriff verweigert: Dir wurde keine Berechtigung zugewiesen. Bitte wende dich an deinen Success Manager.');
            oauthCallbackProcessed.current = false;
            keycloakRedirectAttempted.current = false;
            setProcessingOAuthCallback(false);
            return;
          }
          
          setIsAuthenticated(true);
          setUser(data.user_info);
          localStorage.setItem('authToken', data.token);
          localStorage.setItem('user', JSON.stringify(data.user_info));
          
          // Reset redirect flag on successful authentication
          keycloakRedirectAttempted.current = false;
          // Keep oauthCallbackProcessed as true since we successfully processed it
          // Clear processing state
          setProcessingOAuthCallback(false);
          
          // Show admin panel if user has admin role
          if (clientRole === 'admin') {
            setShowAdminPanel(true);
            fetchUsers();
          }
          
          // Clean up URL - remove code parameter
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          setLoginError(data.message || 'Authentifizierung fehlgeschlagen');
          // Reset flags on error so user can retry
          keycloakRedirectAttempted.current = false;
          oauthCallbackProcessed.current = false;
          setProcessingOAuthCallback(false);
        }
      } else {
        const errorData = await response.json();
        setLoginError(errorData.detail || 'Authentifizierung konnte nicht abgeschlossen werden');
        // Reset flags on error so user can retry
        keycloakRedirectAttempted.current = false;
        oauthCallbackProcessed.current = false;
        setProcessingOAuthCallback(false);
      }
    } catch (err) {
      console.error('OAuth callback error:', err);
      setLoginError('Authentifizierung konnte nicht abgeschlossen werden: ' + err.message);
      // Reset on error so user can retry
      keycloakRedirectAttempted.current = false;
      oauthCallbackProcessed.current = false;
      setProcessingOAuthCallback(false);
    }
  };

  useEffect(() => {
    fetchData();
    checkKeycloakConfig();
    
    // Handle OAuth callback if present - MUST be checked first before any other auth logic
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    
    if (code) {
      // OAuth callback detected - set processing state immediately to show loading
      // instead of login screen, then process the callback
      setProcessingOAuthCallback(true);
      handleOAuthCallback();
      return;
    }
    
    // Only check localStorage if we're not processing an OAuth callback
    const token = localStorage.getItem('authToken');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      const savedUserData = JSON.parse(savedUser);
      
      // Check if user has a valid role
      const clientRole = savedUserData?.client_role;
      if (!clientRole) {
        // User doesn't have a role, clear auth and show error
        setIsAuthenticated(false);
        setUser(null);
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        setLoginError('Zugriff verweigert: Ihnen wurde keine Berechtigung zugewiesen. Bitte wenden Sie sich an Ihren Administrator.');
        return;
      }
      
      setIsAuthenticated(true);
      setUser(savedUserData);
      
      // Show admin panel if user has admin role
      if (clientRole === 'admin') {
        setShowAdminPanel(true);
        fetchUsers();
      }
      
      // Fetch all app IDs from BigQuery
      fetchAllAppIds();
    }
    // Note: Auto-redirect to Keycloak is handled in a separate useEffect
    // to avoid race conditions with OAuth callback processing
  }, []);

  // Auto-redirect to Keycloak if enabled and not authenticated
  // This handles the case when user clicks "Anwendung öffnen" in Keycloak
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const savedUser = localStorage.getItem('user');
    const urlParams = new URLSearchParams(window.location.search);
    const hasCode = urlParams.get('code');
    
    // Check if we're coming from a Keycloak redirect (via referrer)
    // This prevents double redirects when user comes from Drive Workspace
    const referrer = document.referrer;
    const isFromKeycloak = referrer && (
      referrer.includes('keycloak') || 
      referrer.includes('auth') ||
      referrer.includes('sso')
    );
    
    // Only auto-redirect if:
    // - Keycloak is enabled
    // - We have no token
    // - We're not already processing an OAuth callback
    // - We haven't already attempted a redirect (prevents loops)
    // - Loading is complete
    // - We're not coming from a Keycloak redirect (prevents double redirects)
    // - OAuth callback hasn't been processed yet (give it time to complete)
    if (keycloakEnabled && !token && !savedUser && !loading && !hasCode && !keycloakRedirectAttempted.current && !isFromKeycloak && !oauthCallbackProcessed.current) {
      // Add a longer delay to allow OAuth callback processing to complete
      // This prevents race conditions when user comes from Drive Workspace
      keycloakRedirectAttempted.current = true;
      const timer = setTimeout(() => {
        // Double-check that we still don't have a token (callback might have completed)
        const currentToken = localStorage.getItem('authToken');
        const currentUser = localStorage.getItem('user');
        if (!currentToken && !currentUser) {
          handleKeycloakLogin();
        } else {
          // Token was set during the delay, reset the flag
          keycloakRedirectAttempted.current = false;
        }
      }, 500); // Increased delay to 500ms to allow OAuth callback to process
      return () => clearTimeout(timer);
    }
  }, [keycloakEnabled, loading]);

  useEffect(() => {
    // Fetch app IDs when user becomes authenticated
    if (isAuthenticated && user) {
      fetchAllAppIds();
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (user?.username && (user.username === 'highberg' || user.group === 'admin' || user.group === 'highberg')) {
      fetchUsers();
    }
  }, [user?.username, user?.group]);

  useEffect(() => {
    // Clear search when switching away from mapping tab
    if (activeTab !== 'mapping') {
      setAppIdSearch('');
      setSelectedAppId('');
      setMissingProducts([]);
    }
  }, [activeTab]);

  useEffect(() => {
    // Reset when switching mapping modes
    if (mappingMode === 'fill-missing' && selectedAppId) {
      fetchMissingProducts(selectedAppId);
    } else {
      setMissingProducts([]);
    }
  }, [mappingMode]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 16);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleHashChange = () => {
      const isAnmeldung = window.location.hash === '#anmeldung';
      setShowLoginForm(isAnmeldung);
    };
    
    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const useMarketingLanding = !isAuthenticated && !processingOAuthCallback;

  if (loading) {
    return (
      <div className="app">
        <div className="loading">
          <div className="spinner"></div>
          <span>Lade...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={useMarketingLanding ? 'app app-shell app-shell--landing' : 'app'}>
      {useMarketingLanding ? (
        <>
          <LandingHeader
            logoUrl={LOGO_WIDE_URL}
            faviconUrl={FAVICON_URL}
            homeHref={HOME_HREF}
            isAuthenticated={false}
          />
          <div className="home-main--landing">
            {!showLoginForm ? (
              <LandingHero />
            ) : (
              <section 
                id="anmeldung" 
                className="landing-login-section" 
                aria-label="Anmeldung"
              >
                <div className="landing-auth-card-wrap">
                  <div className="auth-card card">
                    <div className="card-header">
                      <h2 className="card-title">Anmeldung</h2>
                      <p className="card-subtitle">Melde dich mit deinem Teamkonto an, um fortzufahren.</p>
                    </div>
                    <div className="card-body">
                      {loginError && (
                        <div className="alert alert-warning">
                          {loginError}
                        </div>
                      )}
                      <form onSubmit={handleLogin} className="auth-form">
                        <div className="form-group">
                          <label htmlFor="username" className="form-label">Benutzername</label>
                          <input
                            type="text"
                            id="username"
                            className="form-control"
                            value={loginForm.username}
                            onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                            placeholder="Benutzername eingeben"
                            autoComplete="username"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="password" className="form-label">Passwort</label>
                          <input
                            type="password"
                            id="password"
                            className="form-control"
                            value={loginForm.password}
                            onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                            placeholder="Passwort eingeben"
                            autoComplete="current-password"
                            required
                          />
                        </div>
                        <button type="submit" className="btn btn-primary btn-full-width">Anmelden</button>
                      </form>
                      {keycloakEnabled && (
                        <div className="keycloak-login-divider">
                          <button
                            type="button"
                            onClick={handleKeycloakLogin}
                            className="btn btn-secondary btn-full-width"
                          >
                            Mit Drive Workspace Anmelden
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            )}
          </div>
          <LandingFooter />
        </>
      ) : (
        <>
      <LandingHeader
        logoUrl={LOGO_WIDE_URL}
        faviconUrl={FAVICON_URL}
        homeHref={HOME_HREF}
        isAuthenticated={isAuthenticated}
        user={user}
        handleLogout={handleLogout}
        currentTabCopy={currentTabCopy}
        isScrolled={isScrolled}
      />
      
      <main className="main">
        {error && (
          <div className="alert alert-warning">
            <p>Fehler: {error}</p>
            <button className="btn btn-primary" onClick={fetchData}>Erneut versuchen</button>
          </div>
        )}
        
        {processingOAuthCallback ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '60vh',
            flexDirection: 'column',
            gap: 'var(--space-4)'
          }}>
            <div className="spinner" style={{ width: '32px', height: '32px', borderWidth: '3px' }}></div>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
              Anmeldung wird verarbeitet...
            </p>
          </div>
        ) : (
          <>
            <div className="app-layout">
            <nav className="nav">
                <ul className="nav-list">
                  <li className="nav-item">
                    <button
                      type="button"
                      title="Systemübersicht"
                      className={`nav-link ${activeTab === 'dashboard' ? 'active' : ''}`}
                      onClick={() => setActiveTab('dashboard')}
                    >
                      <svg className="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <span className="nav-text">Systemübersicht</span>
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      type="button"
                      title="Leuchtturm Metrik"
                      className={`nav-link ${activeTab === 'form' ? 'active' : ''}`}
                      onClick={() => setActiveTab('form')}
                    >
                      <svg className="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <span className="nav-text">Leuchtturm Metrik</span>
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      type="button"
                      title="Produkt-Mappings"
                      className={`nav-link ${activeTab === 'mapping' ? 'active' : ''}`}
                      onClick={() => setActiveTab('mapping')}
                    >
                      <svg className="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      <span className="nav-text">Produkt-Mappings</span>
                    </button>
                  </li>
                  {showAdminPanel && (
                    <li className="nav-item">
                      <button
                        type="button"
                        title="Adminbereich"
                        className={`nav-link ${activeTab === 'admin' ? 'active' : ''}`}
                        onClick={() => setActiveTab('admin')}
                      >
                        <svg className="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="nav-text">Adminbereich</span>
                      </button>
                    </li>
                  )}
                </ul>
            </nav>

            <section className="app-content">
              <div className={`page-header ${isScrolled ? 'page-header-condensed' : ''}`}>
                <div className="page-header-inner">
                  <div className="page-header-meta">
                    <span className="page-kicker">{currentTabCopy.kicker}</span>
                    <h2 className="page-title">{currentTabCopy.title}</h2>
                    <p className="page-description">{pageDescription}</p>
                  </div>
                  <div className="page-header-aside">
                    {activeTab === 'form' && (
                      <div className="page-chip-set">
                        {formSummary && <span className="page-chip">{formSummary}</span>}
                        {hasExistingData && <span className="page-chip page-chip-warning">Bestehende Daten</span>}
                      </div>
                    )}
                    {activeTab === 'mapping' && (
                      <div className="page-chip-set">
                        {mappingSummary && <span className="page-chip">{mappingSummary}</span>}
                        <span className="page-chip">{mappingMode === 'fill-missing' ? 'Fehlende Zuordnungen' : 'Neues Produkt'}</span>
                      </div>
                    )}
                    {activeTab === 'dashboard' && health && (
                      <div className="page-chip-set">
                        <span className="page-chip">Status: {health.status || 'unbekannt'}</span>
                        <span className="page-chip">BigQuery: {health.bigquery || 'unbekannt'}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className={`dashboard-container ${activeTab === 'mapping' ? 'dashboard-container-wide' : ''}`}>
              {activeTab === 'dashboard' && (
                <div className="tab-content">
                  {health && (
                    <div className="card">
                      <div className="card-header">
                        <h2 className="card-title">Systemstatus</h2>
                      </div>
                      <div className="stats-grid">
                        <div className="stat-item">
                          <div className="stat-value">{health.status}</div>
                          <div className="stat-label">Status</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-value">{health.bigquery}</div>
                          <div className="stat-label">BigQuery</div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="card">
                    <div className="card-header">
                      <h2 className="card-title">Schnellaktionen</h2>
                    </div>
                    <div className="quick-actions">
                      <button onClick={() => setActiveTab('form')} className="btn btn-primary">
                        Datenerfassung starten
                      </button>
                      <button onClick={() => setActiveTab('mapping')} className="btn btn-secondary">
                        Produktzuordnungen verwalten
                      </button>
                      {showAdminPanel && (
                        <button onClick={() => setActiveTab('admin')} className="btn btn-secondary">
                          Benutzerverwaltung
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'admin' && showAdminPanel && (
                <div className="card">
                  <div className="card-header">
                    <h2 className="card-title">Adminbereich</h2>
                  </div>
                  
                  <div className="stack-md">
                    <h3>Neuen Nutzer anlegen</h3>
                    <form onSubmit={createUser} className="form-grid two-col">
                      <div className="input-group">
                        <label className="form-label">Benutzername</label>
                        <input
                          type="text"
                          value={newUserForm.username}
                          onChange={(e) => setNewUserForm({...newUserForm, username: e.target.value})}
                          className="form-control"
                        />
                      </div>
                      <div className="input-group">
                        <label className="form-label">Name</label>
                        <input
                          type="text"
                          value={newUserForm.name}
                          onChange={(e) => setNewUserForm({...newUserForm, name: e.target.value})}
                          className="form-control"
                        />
                      </div>
                      <div className="input-group">
                        <label className="form-label">Gruppe</label>
                        <input
                          type="text"
                          value={newUserForm.group}
                          onChange={(e) => setNewUserForm({...newUserForm, group: e.target.value})}
                          className="form-control"
                        />
                      </div>
                      <div className="input-group button-group button-group-full-width">
                      <button type="submit" className="btn btn-primary">
                        Nutzer erstellen
                      </button>
                      </div>
                    </form>
                  </div>
                  
                  <div className="stack-md">
                    <h3>Bestehende Nutzer</h3>
                    <div className="data-table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Benutzername</th>
                            <th>Name</th>
                            <th>Gruppe</th>
                            <th>Aktionen</th>
                          </tr>
                        </thead>
                        <tbody>
                          {users.map(user => (
                            <tr key={user.username}>
                              <td>{user.username}</td>
                              <td>{user.name}</td>
                              <td>{user.group}</td>
                              <td>
                                <button 
                                  onClick={() => deleteUser(user.username)}
                                  className="btn btn-sm btn-outline"
                                >
                                  Löschen
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'form' && (
                <div className="form-layout">
                  <aside className="form-sidebar">
                    <div className="card sticky-card">
                      <div className="card-header">
                        <span className="card-eyebrow">Vorbereitung</span>
                        <h2 className="card-title">Zeitraum festlegen</h2>
                      </div>
                      <div className="card-body form-sidebar-body">
                        <div className="form-sidebar-section">
                          <h3 className="form-sidebar-section-title">Publisher &amp; Jahr</h3>
                          <div className="form-sidebar-grid">
                            <div className="input-group">
                              <label className="form-label">Publisher auswählen</label>
                              <select
                                value={selectedPublisher}
                                onChange={(e) => {
                                  const nextPublisher = e.target.value;
                                  setSelectedPublisher(nextPublisher);
                                  setSelectedMonth('');
                                  setHasExistingData(false);
                                  setExistingDataWarning('');
                                  setFormData(createEmptyFormData());

                                  if (nextPublisher && user?.allowed_publisher_ids?.[nextPublisher]) {
                                    const publisherData = user.allowed_publisher_ids[nextPublisher];
                                    if (publisherData?.group) {
                                      fetchMissingMonths(nextPublisher, publisherData.group, selectedYear);
                                    }
                                  } else {
                                    setMissingMonths([]);
                                  }
                                }}
                                className="form-control"
                              >
                              <option value="">Publisher wählen...</option>
                                {Object.keys(user.allowed_publisher_ids).map(publisher => (
                                  <option key={publisher} value={publisher}>{publisher}</option>
                                ))}
                              </select>
                            </div>
                            <div className="input-group">
                              <label className="form-label">Jahr auswählen</label>
                              <input
                                type="number"
                                value={selectedYear}
                                onChange={(e) => {
                                  const currentYear = new Date().getFullYear();
                                  const nextYear = Math.min(parseInt(e.target.value, 10) || currentYear, currentYear);
                                  setSelectedYear(nextYear);
                                  setSelectedMonth('');
                                  setHasExistingData(false);
                                  setExistingDataWarning('');
                                  if (selectedPublisher && user?.allowed_publisher_ids?.[selectedPublisher]) {
                                    const publisherData = user.allowed_publisher_ids[selectedPublisher];
                                    if (publisherData?.group) {
                                      fetchMissingMonths(selectedPublisher, publisherData.group, nextYear);
                                    }
                                  }
                                }}
                                min="2020"
                                max={new Date().getFullYear()}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="form-sidebar-section">
                          <h3 className="form-sidebar-section-title">Monat auswählen</h3>
                          <div className="input-group">
                            {(() => {
                              const allMonths = [
                                'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
                                'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
                              ];
                              const now = new Date();
                              const maxMonthIndex = selectedYear < now.getFullYear()
                                ? 11
                                : now.getMonth(); // 0-based: current month is the last allowed
                              const availableMonths = allMonths.slice(0, maxMonthIndex + 1);
                              return (
                                <select
                                  value={selectedMonth}
                                  onChange={(e) => {
                                    const nextMonth = e.target.value;
                                    setSelectedMonth(nextMonth);
                                    setHasExistingData(false);
                                    setExistingDataWarning('');
                                    if (nextMonth && selectedPublisher && user?.allowed_publisher_ids?.[selectedPublisher]) {
                                      const publisherData = user.allowed_publisher_ids[selectedPublisher];

                                      if (publisherData?.publisher_id) {
                                        const actualPublisher = Object.keys(user.allowed_publisher_ids).find(key =>
                                          user.allowed_publisher_ids[key]?.publisher_id === publisherData.publisher_id &&
                                          key !== user.username
                                        ) || selectedPublisher;

                                        const actualGroup = publisherData.group;

                                        fetchExistingData(actualPublisher, publisherData.publisher_id, actualGroup, selectedYear, nextMonth);
                                      }
                                    }
                                  }}
                                  className="form-control"
                                >
                                  <option value="">Monat wählen...</option>
                                  {availableMonths.map(month => (
                                    <option key={month} value={month}>{month}</option>
                                  ))}
                                </select>
                              );
                            })()}
                          </div>
                          <div style={{ 
                            marginTop: 'var(--space-3)', 
                            padding: 'var(--space-3)', 
                            backgroundColor: 'var(--color-primary-light-2)', 
                            borderRadius: 'var(--border-radius-md)',
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-text-secondary)',
                            lineHeight: '1.5'
                          }}>
                            <strong>Hinweis:</strong> Die Eintragung erfolgt immer zum Stand des 1. des jeweiligen Monats. 
                            Beispiel: November-Zahlen werden zum Stand 01.12. in den Dezember-Reiter eingetragen.
                          </div>
                        </div>

                        {selectedPublisher && (
                          <div className="form-sidebar-section">
                            <h3 className="form-sidebar-section-title">Status</h3>
                            {missingMonths.length > 0 ? (
                              <div className="form-sidebar-meta">
                                <span className="sidebar-meta-title">Fehlende Monate {selectedYear}</span>
                                <div className="sidebar-chip-group">
                                  {missingMonths.map((month) => (
                                    <span key={month} className="sidebar-chip">{month}</span>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <div className="form-sidebar-meta form-sidebar-meta-success">
                                <span className="sidebar-meta-title">Alle Monate sind erfasst</span>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="form-sidebar-section">
                          <h3 className="form-sidebar-section-title">Aktuelle Auswahl</h3>
                          <div className="form-sidebar-summary">
                            <div className="sidebar-chip-group">
                              <span className="sidebar-chip">{selectedPublisher || 'Kein Publisher'}</span>
                              <span className="sidebar-chip">{selectedMonth || 'Monat offen'}</span>
                              <span className="sidebar-chip">{selectedYear}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </aside>

                  <section className="form-main card">
                    <div className="card-header form-main-header">
                      <div>
                        <span className="card-eyebrow">Dateneingabe</span>
                        <h2 className="card-title">Daten eingeben</h2>
                      </div>
                      <div className="form-main-summary">
                        {formSummary && <span className="page-chip">{formSummary}</span>}
                        {hasExistingData && <span className="page-chip page-chip-warning">Bestehende Daten</span>}
                      </div>
                    </div>
                    <div className="card-body">
                      {isFormReady ? (
                        <>
                          {existingDataWarning && (
                            <div className="alert alert-info form-alert">
                              <p>{existingDataWarning}</p>
                            </div>
                          )}
                          <div className="form-data-grid">
                            {formData.web_abos_data.map((abo, index) => (
                              <div key={abo.abo_type} className="form-data-card">
                                <div className="form-data-title-wrapper">
                                  <h4 className="form-data-title">
                                    Web-Abos {abo.abo_type}
                                    {getPKTooltip(abo.abo_type) && (
                                      <span className="tooltip-icon" title={getPKTooltip(abo.abo_type)}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="currentColor"/>
                                        </svg>
                                      </span>
                                    )}
                                  </h4>
                                  {getPKTitle(abo.abo_type) && (
                                    <p className="form-data-subtitle">{getPKTitle(abo.abo_type)}</p>
                                  )}
                                </div>
                                <div className="form-data-row">
                                  <div className="form-data-field">
                                    <label className="form-data-label">Aktuell</label>
                                    <MetricNumberInput
                                      value={abo.aktuell}
                                      onChange={(nextValue) => {
                                        const newFormData = {...formData};
                                        newFormData.web_abos_data[index].aktuell = nextValue;
                                        setFormData(newFormData);
                                      }}
                                      className="form-data-input"
                                    />
                                  </div>
                                  <div className="form-data-field">
                                    <label className="form-data-label">Zugänge</label>
                                    <MetricNumberInput
                                      value={abo.zugaenge}
                                      onChange={(nextValue) => {
                                        const newFormData = {...formData};
                                        newFormData.web_abos_data[index].zugaenge = nextValue;
                                        setFormData(newFormData);
                                      }}
                                      className="form-data-input"
                                    />
                                  </div>
                                  <div className="form-data-field">
                                    <label className="form-data-label">Abgänge</label>
                                    <MetricNumberInput
                                      value={abo.abgaenge}
                                      onChange={(nextValue) => {
                                        const newFormData = {...formData};
                                        newFormData.web_abos_data[index].abgaenge = nextValue;
                                        setFormData(newFormData);
                                      }}
                                      className="form-data-input"
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}

                            {formData.epaper_abos_data.map((abo, index) => (
                              <div key={abo.abo_type} className="form-data-card">
                                <div className="form-data-title-wrapper">
                                  <h4 className="form-data-title">
                                    ePaper-Abos
                                    {getPKTooltip(abo.abo_type) && (
                                      <span className="tooltip-icon" title={getPKTooltip(abo.abo_type)}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="currentColor"/>
                                        </svg>
                                      </span>
                                    )}
                                  </h4>
                                  {getPKTitle(abo.abo_type) && (
                                    <p className="form-data-subtitle">{getPKTitle(abo.abo_type)}</p>
                                  )}
                                </div>
                                <div className="form-data-row">
                                  <div className="form-data-field">
                                    <label className="form-data-label">Aktuell</label>
                                    <MetricNumberInput
                                      value={abo.aktuell}
                                      onChange={(nextValue) => {
                                        const newFormData = {...formData};
                                        newFormData.epaper_abos_data[index].aktuell = nextValue;
                                        setFormData(newFormData);
                                      }}
                                      className="form-data-input"
                                    />
                                  </div>
                                  <div className="form-data-field">
                                    <label className="form-data-label">Zugänge</label>
                                    <MetricNumberInput
                                      value={abo.zugaenge}
                                      onChange={(nextValue) => {
                                        const newFormData = {...formData};
                                        newFormData.epaper_abos_data[index].zugaenge = nextValue;
                                        setFormData(newFormData);
                                      }}
                                      className="form-data-input"
                                    />
                                  </div>
                                  <div className="form-data-field">
                                    <label className="form-data-label">Abgänge</label>
                                    <MetricNumberInput
                                      value={abo.abgaenge}
                                      onChange={(nextValue) => {
                                        const newFormData = {...formData};
                                        newFormData.epaper_abos_data[index].abgaenge = nextValue;
                                        setFormData(newFormData);
                                      }}
                                      className="form-data-input"
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="form-empty-state">
                          <h3>Starte mit der Auswahl</h3>
                          <p>Wähle im linken Bereich einen Publisher, das Jahr und den Monat, um Abodaten zu pflegen.</p>
                        </div>
                      )}
                    </div>
                    <div className="card-footer form-actions">
                      <button 
                        onClick={submitForm} 
                        className="btn btn-primary"
                        disabled={!isFormReady}
                      >
                        {primaryActionLabel}
                      </button>
                      <button 
                        onClick={() => {
                          setFormData(createEmptyFormData());
                          setHasExistingData(false);
                          setExistingDataWarning('');
                        }}
                        className="btn btn-secondary"
                        disabled={!isFormReady}
                      >
                        Formular leeren
                      </button>
                    </div>
                  </section>
                </div>
              )}

              {activeTab === 'mapping' && (
                <div className="tab-content">
                <div className="card">
                  <div className="card-header">
                      <h2 className="card-title">Produkt-Mappings</h2>
                  </div>
                  
                    <div className="card-body">
                      {/* Mode Switcher */}
                      <div className="form-group">
                        <label className="form-label">Modus auswählen</label>
                        <div className="mapping-mode-switcher">
                          <button
                            type="button"
                            onClick={() => {
                              setMappingMode('fill-missing');
                              setSelectedAppId('');
                              setMappingForm({ product_name: '', abo_type: '', product_category: '' });
                            }}
                            className={`btn ${mappingMode === 'fill-missing' ? 'btn-primary' : 'btn-secondary'}`}
                          >
                            Fehlende Produkt-Mappings ausfüllen
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setMappingMode('create-new');
                              setSelectedAppId('');
                              setMappingForm({ product_name: '', abo_type: '', product_category: '' });
                              setMissingProducts([]);
                            }}
                            className={`btn ${mappingMode === 'create-new' ? 'btn-primary' : 'btn-secondary'}`}
                          >
                            Neues Produkt anlegen
                          </button>
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">App-ID auswählen</label>
                        {loadingAppIds ? (
                          <div className="loading">
                            <div className="spinner"></div>
                            <span>App-IDs werden geladen...</span>
                          </div>
                        ) : (
                          <>
                            <div className="app-id-search-container">
                              <input
                                type="text"
                                placeholder="App ID durchsuchen..."
                                value={appIdSearch}
                                onChange={(e) => setAppIdSearch(e.target.value)}
                                className="form-control app-id-search-input"
                              />
                            </div>
                            
                            <div className="app-id-count-text">
                              {filteredAppIds.length} von {allAppIds.length || (user?.allowed_app_ids?.length || 0)} App-IDs
                            </div>
                            
                            {filteredAppIds.length === 0 ? (
                              <div className="alert alert-warning">
                                <p>Keine App IDs gefunden für "{appIdSearch}"</p>
                                <button
                                  onClick={() => setAppIdSearch('')}
                                  className="btn btn-sm btn-secondary"
                                >
                                  Suche zurücksetzen
                                </button>
                              </div>
                            ) : (
                              <div className="app-id-scroll">
                                {filteredAppIds.map(appId => (
                                  <button
                                    key={appId}
                                    type="button"
                                    onClick={() => handleAppIdSelect(appId)}
                                    className={`app-id-item ${selectedAppId === appId ? 'selected' : ''}`}
                                  >
                                    <span className="app-id-item-text">{appId}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Products View Toggle - Only show when app is selected and in fill-missing mode */}
                  {selectedAppId && mappingMode === 'fill-missing' && (
                    <div className="card">
                      <div className="card-header">
                        <div className="products-view-header">
                          <h2 className="card-title">Produkt-Mappings</h2>
                          <div className="selected-app-id-badge">{selectedAppId}</div>
                        </div>
                        <div className="products-view-toggles">
                          <button
                            type="button"
                            onClick={() => {
                              setProductsView('missing');
                              if (missingProducts.length === 0) {
                                fetchMissingProducts(selectedAppId);
                              }
                            }}
                            className={`toggle-button ${productsView === 'missing' ? 'active' : ''}`}
                          >
                            Fehlende Produkt-Mappings
                            {missingProducts.length > 0 && (
                              <span className="toggle-badge">{missingProducts.length}</span>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setProductsView('existing');
                              if (existingProducts.length === 0) {
                                fetchExistingProducts(selectedAppId);
                              }
                            }}
                            className={`toggle-button ${productsView === 'existing' ? 'active' : ''}`}
                          >
                            Bereits gemappte Produkte
                            {existingProducts.length > 0 && (
                              <span className="toggle-badge">{existingProducts.length}</span>
                            )}
                          </button>
                        </div>
                      </div>
                      <div className="card-body">
                        {productsView === 'missing' ? (
                          <>
                            {loadingMissingProducts ? (
                              <div className="loading">
                                <div className="spinner"></div>
                                <span>Lade fehlende Produkte...</span>
                              </div>
                            ) : missingProducts.length === 0 ? (
                              <div className="alert alert-info">
                                <p>Keine fehlenden Produkt-Mappings gefunden für <strong>{selectedAppId}</strong></p>
                              </div>
                            ) : (
                              <div className="data-table-container">
                                <div className="table-toolbar">
                                  {editingMissingKeys.size > 0 ? (
                                    <>
                                      <button
                                        type="button"
                                        onClick={handleSaveAllMissing}
                                        className="btn btn-sm btn-primary"
                                        disabled={savingAll}
                                      >
                                        {savingAll ? 'Speichern...' : 'Alle speichern'}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={closeAllMissingEdit}
                                        className="btn btn-sm btn-secondary"
                                        disabled={savingAll}
                                      >
                                        Abbrechen
                                      </button>
                                    </>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={openAllMissingForEdit}
                                      className="btn btn-sm btn-outline"
                                    >
                                      Alle bearbeiten
                                    </button>
                                  )}
                                </div>
                                <table className="data-table">
                                  <thead>
                                    <tr>
                                      <th>Produkt</th>
                                      <th>Conversions</th>
                                      <th>Drive</th>
                                      <th>Kategorie</th>
                                      <th>Aktionen</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {missingProducts.map((product, index) => {
                                      const rowKey = `${product.app_id}-${product.subscription_conversion_product_publisher}`;
                                      return (
                                        <MissingProductRow
                                          key={`${rowKey}-${index}`}
                                          product={product}
                                          onUpdate={updateMissingProduct}
                                          forceEditing={editingMissingKeys.has(rowKey)}
                                          onExitEdit={() => setEditingMissingKeys((prev) => { const n = new Set(prev); n.delete(rowKey); return n; })}
                                          saveAllTrigger={saveAllMissingTrigger}
                                          onSaveComplete={onSaveAllComplete}
                                        />
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            {loadingExistingProducts ? (
                              <div className="loading">
                                <div className="spinner"></div>
                                <span>Lade bestehende Produkte...</span>
                              </div>
                            ) : existingProducts.length === 0 ? (
                              <div className="alert alert-info">
                                <p>Noch keine Produkte für <strong>{selectedAppId}</strong> gemappt</p>
                              </div>
                            ) : (
                              <div className="data-table-container">
                                <div className="table-toolbar">
                                  {editingExistingKeys.size > 0 ? (
                                    <>
                                      <button
                                        type="button"
                                        onClick={handleSaveAllExisting}
                                        className="btn btn-sm btn-primary"
                                        disabled={savingAll}
                                      >
                                        {savingAll ? 'Speichern...' : 'Alle speichern'}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={closeAllExistingEdit}
                                        className="btn btn-sm btn-secondary"
                                        disabled={savingAll}
                                      >
                                        Abbrechen
                                      </button>
                                    </>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={openAllExistingForEdit}
                                      className="btn btn-sm btn-outline"
                                    >
                                      Alle bearbeiten
                                    </button>
                                  )}
                                </div>
                                <table className="data-table">
                                  <thead>
                                    <tr>
                                      <th>Produkt</th>
                                      <th>Drive</th>
                                      <th>Kategorie</th>
                                      <th>Aktionen</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {existingProducts.map((product, index) => {
                                      const rowKey = product.subscription_conversion_product_publisher;
                                      return (
                                        <ExistingProductRow
                                          key={`existing-${rowKey}-${index}`}
                                          product={product}
                                          appId={selectedAppId}
                                          onUpdate={updateExistingProduct}
                                          forceEditing={editingExistingKeys.has(rowKey)}
                                          onExitEdit={() => setEditingExistingKeys((prev) => { const n = new Set(prev); n.delete(rowKey); return n; })}
                                          saveAllTrigger={saveAllExistingTrigger}
                                          onSaveComplete={onSaveAllComplete}
                                        />
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Existing Products - Shown when app is selected but not in fill-missing mode */}
                  {selectedAppId && mappingMode !== 'fill-missing' && (
                    <div className="card">
                      <div className="card-header">
                        <div className="products-view-header">
                          <h2 className="card-title">Bereits gemappte Produkte</h2>
                          <div className="selected-app-id-badge">{selectedAppId}</div>
                        </div>
                      </div>
                      <div className="card-body">
                        {loadingExistingProducts ? (
                          <div className="loading">
                            <div className="spinner"></div>
                            <span>Lade bestehende Produkte...</span>
                          </div>
                        ) : existingProducts.length === 0 ? (
                          <div className="alert alert-info">
                            <p>Noch keine Produkte für <strong>{selectedAppId}</strong> gemappt</p>
                          </div>
                        ) : (
                          <div className="data-table-container">
                            <div className="table-toolbar">
                              {editingExistingKeys.size > 0 ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={handleSaveAllExisting}
                                    className="btn btn-sm btn-primary"
                                    disabled={savingAll}
                                  >
                                    {savingAll ? 'Speichern...' : 'Alle speichern'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={closeAllExistingEdit}
                                    className="btn btn-sm btn-secondary"
                                    disabled={savingAll}
                                  >
                                    Abbrechen
                                  </button>
                                </>
                              ) : (
                                <button
                                  type="button"
                                  onClick={openAllExistingForEdit}
                                  className="btn btn-sm btn-outline"
                                >
                                  Alle bearbeiten
                                </button>
                              )}
                            </div>
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>Produkt</th>
                                  <th>Drive</th>
                                  <th>Kategorie</th>
                                  <th>Aktionen</th>
                                </tr>
                              </thead>
                              <tbody>
                                {existingProducts.map((product, index) => {
                                  const rowKey = product.subscription_conversion_product_publisher;
                                  return (
                                    <ExistingProductRow
                                      key={`existing-${rowKey}-${index}`}
                                      product={product}
                                      appId={selectedAppId}
                                      onUpdate={updateExistingProduct}
                                      forceEditing={editingExistingKeys.has(rowKey)}
                                      onExitEdit={() => setEditingExistingKeys((prev) => { const n = new Set(prev); n.delete(rowKey); return n; })}
                                      saveAllTrigger={saveAllExistingTrigger}
                                      onSaveComplete={onSaveAllComplete}
                                    />
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Create New Product Mode */}
                  {mappingMode === 'create-new' && selectedAppId && (
                    <div className="card">
                      <div className="card-header">
                        <h2 className="card-title">Neues Produkt anlegen</h2>
                        <div className="selected-app-id-badge">{selectedAppId}</div>
                      </div>
                      <div className="card-body">
                      <form onSubmit={createMapping} className="form-grid two-col">
                        <div className="input-group">
                            <label className="form-label">Produktname</label>
                          <input
                            type="text"
                            value={mappingForm.product_name}
                            onChange={(e) => setMappingForm({...mappingForm, product_name: e.target.value})}
                            className="form-control"
                              placeholder="z.B. PK1, PK2, PK3"
                            required
                          />
                        </div>
                        <div className="input-group">
                          <label className="form-label">Aboart</label>
                          <select
                            value={mappingForm.abo_type}
                            onChange={(e) => setMappingForm({...mappingForm, abo_type: e.target.value})}
                            className="form-control"
                            required
                          >
                              <option value="">Aboart auswählen...</option>
                            <option value="Vollabo">Vollabo</option>
                            <option value="Probeabo">Probeabo</option>
                            <option value="Sonstiges">Sonstiges</option>
                          </select>
                        </div>
                        <div className="input-group">
                          <label className="form-label">Kategorie</label>
                          <select
                            value={mappingForm.product_category}
                            onChange={(e) => setMappingForm({...mappingForm, product_category: e.target.value})}
                            className="form-control"
                            required
                          >
                              <option value="">Kategorie auswählen...</option>
                            <option value="Online">Online</option>
                            <option value="ePaper">ePaper</option>
                            <option value="Print">Print</option>
                            <option value="Sonstiges">Sonstiges</option>
                          </select>
                        </div>
                          <div className="input-group button-group button-group-full-width">
                        <button type="submit" className="btn btn-primary">
                              Produkt anlegen
                        </button>
                          </div>
                      </form>
                      </div>
                    </div>
                  )}
                </div>
              )}
              </div>
            </section>
          </div>
          </>
        )}
      </main>
      </>
      )}
    </div>
  );
}

export default App;
